import {
  createDate,
  createMonth,
  formatDate,
  getMonthNumberOfDays,
  getMonthesNames,
  getWeekDaysNames,
  getYearsInterval,
} from '..';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { UseCalendarProps } from '../../model/types';

const formatSingleDateInput = (date: Date) => formatDate(date, 'DD/MM/YYYY');

type CalendarNavState = {
  selectedDate: ReturnType<typeof createDate>;
  selectedMonth: ReturnType<typeof createMonth>;
  selectedYear: number;
  selectedYearInterval: number[];
};

const buildCalendarNavState = (date: Date, locale: string): CalendarNavState => {
  const selectedDate = createDate({ date });
  return {
    selectedDate,
    selectedMonth: createMonth({ date: new Date(selectedDate.year, selectedDate.monthIndex), locale }),
    selectedYear: selectedDate.year,
    selectedYearInterval: getYearsInterval(selectedDate.year),
  };
};

export const useDatePickerHook = (props: UseCalendarProps) => {
  const { firstWeekDay = 2, locale = 'defult', selectedDate: date, isRange = true } = props;
  const calendarDate = date ?? new Date();
  const initialNavState = buildCalendarNavState(calendarDate, locale);

  /**
   * стэйт для выбора разных режимов просмотра календаря
   */
  const [mode, setMode] = useState<'days' | 'monthes' | 'years'>('days');
  /**
   * отслеживаем выбранную дату
   */
  const [selectedDate, setSelectedDate] = useState(initialNavState.selectedDate);
  const [selectedMonth, setSelectedMonth] = useState(initialNavState.selectedMonth);
  const [selectedYear, setSelectedYear] = useState(initialNavState.selectedYear);
  const [selectedYearInterval, setSelectedYearInterval] = useState(initialNavState.selectedYearInterval);
  const [selectedRange, setSelectedRange] = useState<[Date | null, Date | null]>(
    !isRange && date ? [date, date] : [null, null]
  );
  const [prevDayInput, setPrevDayInput] = useState(!isRange && date ? formatSingleDateInput(date) : '');

  useEffect(() => {
    if (isRange) {
      return;
    }

    if (date) {
      const nextNavState = buildCalendarNavState(date, locale);
      setSelectedDate(nextNavState.selectedDate);
      setSelectedMonth(nextNavState.selectedMonth);
      setSelectedYear(nextNavState.selectedYear);
      setSelectedYearInterval(nextNavState.selectedYearInterval);
      setPrevDayInput(formatSingleDateInput(date));
      setSelectedRange([date, date]);
      return;
    }

    const fallbackNavState = buildCalendarNavState(new Date(), locale);
    setSelectedDate(fallbackNavState.selectedDate);
    setSelectedMonth(fallbackNavState.selectedMonth);
    setSelectedYear(fallbackNavState.selectedYear);
    setSelectedYearInterval(fallbackNavState.selectedYearInterval);
    setPrevDayInput('');
    setSelectedRange([null, null]);
  }, [date, isRange, locale]);

  const monthesNames = useMemo(() => getMonthesNames(locale), [locale]);
  const weekDaysNames = useMemo(() => getWeekDaysNames(firstWeekDay, locale), [firstWeekDay, locale]);
  const days = useMemo(() => selectedMonth.createMonthDays(), [selectedMonth]);
  const calendarDays = useMemo(() => {
    /**
     * получаем кол-во дней в данном месяце
     */
    const monthNumberOfDays = getMonthNumberOfDays(selectedMonth.monthIndex, selectedYear);

    const prevMonthDays = createMonth({
      date: new Date(selectedYear, selectedMonth.monthIndex - 1),
      locale,
    }).createMonthDays();

    const nextMonthDays = createMonth({
      date: new Date(selectedYear, selectedMonth.monthIndex + 1),
      locale,
    }).createMonthDays();

    const firstDay = days[0];
    const lastDay = days[monthNumberOfDays - 1];

    /**
     * вырезаем индекс для корректного отображения дней недели в календаре
     */
    const shiftIndex = firstWeekDay - 1;

    /**
     * дни предыдущего месяца которые должны быть отображены в календаре
     */
    const numberOfPrevDays =
      firstDay.dayNumberInWeek - 1 - shiftIndex < 0
        ? 7 - (firstWeekDay - firstDay.dayNumberInWeek)
        : firstDay.dayNumberInWeek - 1 - shiftIndex;

    /**
     * дни след месяца которые должны быть отображены в календаре
     */
    const numberOfNextDays =
      7 - lastDay.dayNumberInWeek + shiftIndex > 6
        ? 7 - lastDay.dayNumberInWeek - (7 - shiftIndex)
        : 7 - lastDay.dayNumberInWeek + shiftIndex;

    /**
     * все дни которые должны быть отображены в календаре
     */
    const totalCalendarDays = days.length + numberOfNextDays + numberOfPrevDays;

    const result = [];
    for (let i = 0; i < numberOfPrevDays; i += 1) {
      const inverted = numberOfPrevDays - i;
      result[i] = prevMonthDays[prevMonthDays.length - inverted];
    }
    for (let i = numberOfPrevDays; i < totalCalendarDays - numberOfNextDays; i += 1) {
      result[i] = days[i - numberOfPrevDays];
    }
    for (let i = totalCalendarDays - numberOfNextDays; i < totalCalendarDays; i += 1) {
      result[i] = nextMonthDays[i - totalCalendarDays + numberOfNextDays];
    }
    return result;
  }, [selectedMonth.monthIndex, selectedYear, days, firstWeekDay, locale]);

  const onClickArrow = (direction: 'right' | 'left') => {
    if (mode === 'monthes' && direction === 'left') {
      const year = selectedYear - 1;
      if (!selectedYearInterval.includes(year)) setSelectedYearInterval(getYearsInterval(year));
      return setSelectedYear(selectedYear - 1);
    }

    if (mode === 'monthes' && direction === 'right') {
      const year = selectedYear + 1;
      if (!selectedYearInterval.includes(year)) setSelectedYearInterval(getYearsInterval(year));
      return setSelectedYear(selectedYear + 1);
    }

    if (mode === 'days') {
      /**
       * в зависимости от того куда тыкнул пользователь к месяцу добавляем или убавляем 1
       */
      const monthIndex = direction === 'left' ? selectedMonth.monthIndex - 1 : selectedMonth.monthIndex + 1;
      /**
       * обрабатываем состояние перехода в предыдущий год
       */
      if (monthIndex === -1) {
        const year = selectedYear - 1;
        /**
         * обновляем состояние
         */
        setSelectedYear(year);
        /**
         * проверка на наличие года в интервале , если есть то обновляем интервал
         */
        if (!selectedYearInterval.includes(year)) setSelectedYearInterval(getYearsInterval(year));

        /**
         * обновляем месяц в котором мы находимся
         */
        return setSelectedMonth(createMonth({ date: new Date(selectedYear - 1, 11), locale }));
      }

      /**
       * обрабатываем состояние перехода в следующий год
       */
      if (monthIndex === 12) {
        const year = selectedYear + 1;
        setSelectedYear(year);
        if (!selectedYearInterval.includes(year)) setSelectedYearInterval(getYearsInterval(year));
        return setSelectedMonth(createMonth({ date: new Date(year, 0), locale }));
      }

      setSelectedMonth(createMonth({ date: new Date(selectedYear, monthIndex), locale }));
    }
  };

  /**
   * в зависимости от индекса месяца создается новый месяц и сетится в наш стэйт
   */
  const setSelectedMonthByIndex = (monthIndex: number) => {
    setSelectedMonth(createMonth({ date: new Date(selectedYear, monthIndex), locale }));
  };

  const onDayClickHandler = (
    day: Date,
    isRange: boolean,
    setDayInInput: Dispatch<SetStateAction<string>>,
    setIsCalendarVisible: Dispatch<SetStateAction<boolean>>
  ) => {
    /**
     * задаем данное условие для избежания бага при выборе одной даты
     */
    if (!isRange) {
      setSelectedRange([day, day]);
      props.setNewSelectedDate?.([day, day]);

      const formattedDate = formatDate(day, 'DD/MM/YYYY');
      setPrevDayInput(`${formattedDate}`);
      setIsCalendarVisible(false);
      return;
    }

    setSelectedRange((prevRange) => {
      if (!prevRange[0] || (prevRange[0] && prevRange[1])) {
        /**
         * Если первая дата не установлена или установлена пара дат, устанавливаем первую дату
         */
        const dateStart = formatDate(day, 'DD/MM/YYYY');
        setDayInInput(dateStart);
        const newDateRange: [Date | null, Date | null] = [day, null];
        props.setNewSelectedDate?.(newDateRange);
        return newDateRange;
      } else if (day < prevRange[0]) {
        /**
         * Если выбранная дата раньше первой даты в диапазоне, обновляем инпут и устанавливаем диапазон
         */
        const dateStart = formatDate(day, 'DD/MM/YYYY');
        const dateEnd = formatDate(prevRange[0], 'DD/MM/YYYY');
        setIsCalendarVisible(false);
        setPrevDayInput(`${dateStart}  -  ${dateEnd}`);
        const newDateRange: [Date | null, Date | null] = [day, prevRange[0]];
        props.setNewSelectedDate?.(newDateRange);
        return newDateRange;
      } else {
        /**
         * Иначе, устанавливаем вторую дату в диапазоне и обновляем инпут
         */
        const dateStart = formatDate(prevRange[0], 'DD/MM/YYYY');
        const dateEnd = formatDate(day, 'DD/MM/YYYY');
        setIsCalendarVisible(false);
        setPrevDayInput(`${dateStart} - ${dateEnd}`);
        const newDateRange: [Date | null, Date | null] = [prevRange[0], day];
        props.setNewSelectedDate?.(newDateRange);
        return newDateRange;
      }
    });
  };

  return {
    state: {
      mode,
      calendarDays,
      weekDaysNames,
      monthesNames,
      selectedDate,
      selectedMonth,
      selectedYear,
      selectedYearInterval,
      selectedRange,
      prevDayInput,
    },
    functions: {
      setMode,
      setSelectedDate,
      onClickArrow,
      setSelectedMonthByIndex,
      onDayClickHandler,
      setPrevDayInput,
      setSelectedRange,
    },
  };
};
